/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
/* eslint-disable operator-linebreak */
// eslint-disable-next-line max-classes-per-file
import { HttpStatusCode } from 'axios';
import {
    AgencySettings,
    APIResponse, BffSession, ComplianceAgency, LoginSystem,
    UserExperiments,
} from '@acp/shared';
import { getBFFApiClient } from './utils/api-client';

export class SessionError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SessionError';
    }
}

export class LoginError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'LoginError';
    }
}

export class LogoutError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'LogoutError';
    }
}

export class SwitchAgencyError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SwitchAgencyError';
    }
}

export async function login(
    token: string,
    complianceSystemSessionId: string | null,
    isImpersonating: boolean,
    originalComplianceUserId: string | null,
    originalComplianceUserIdSignature: string | null,
    loginIdUsername = false
) {
    const response = await getBFFApiClient().post<APIResponse<BffSession>>('/v1/auth/login', {
        authToken: token,
        complianceSystemSessionId,
        isImpersonating,
        originalComplianceUserId,
        originalComplianceUserIdSignature,
        loginIdUsername,
    });

    if (response.status !== HttpStatusCode.Ok) {
        throw new LoginError('Failed to login');
    }

    return response.data.data;
}

export async function dismissUsernameLoginBanner() {
    const response = await getBFFApiClient().post<APIResponse<BffSession>>(
        '/v1/auth/username-login-banner/dismiss'
    );

    if (response.status !== HttpStatusCode.Ok || response.data.success === false) {
        throw new Error('Failed to dismiss username login banner');
    }

    return response.data.data;
}

export async function logout() {
    const response = await getBFFApiClient().post<APIResponse<{
        logoutUrl: string;
    }>>('/v1/auth/logout');

    if (response.status !== HttpStatusCode.Ok) {
        throw new LogoutError('Failed to logout');
    }

    return response.data;
}

export async function getSession(): Promise<BffSession> {
    const response = await getBFFApiClient().get<APIResponse<BffSession>>('/v1/auth/session');

    if (
        response.status !== HttpStatusCode.Ok ||
        response.data.success === false
    ) {
        throw new SessionError('Session not found');
    }

    return response.data.data;
}

export async function getAllSystemStates(): Promise<LoginSystem[]> {
    const response = await getBFFApiClient().get<APIResponse<LoginSystem[]>>('/v1/auth/systems');

    if (response.data.success === false) {
        throw new Error(`Error fetching systems: ${response.data.error}`);
    }

    return response.data.data;
}

export async function getUserAgencies(): Promise<ComplianceAgency[]> {
    const response = await getBFFApiClient().get<APIResponse<ComplianceAgency[]>>(
        '/v1/auth/agencies'
    );

    if (response.data.success === false) {
        throw new Error(`Error fetching agencies: ${response.data.error}`);
    }

    return response.data.data;
}

export async function switchUserAgency(newAgencyId: number) {
    const response = await getBFFApiClient().put<APIResponse<BffSession>>(
        '/v1/auth/agencies',
        {
            agencyId: newAgencyId,
        }
    );

    if (
        response.status !== HttpStatusCode.Ok ||
        response.data.success === false
    ) {
        throw new SwitchAgencyError('Failed to switch agency');
    }

    return response.data.data;
}

export async function getActiveExperiments() {
    const response = await getBFFApiClient().get<APIResponse<UserExperiments>>('/v1/me/active-experiments');

    if (response.status !== HttpStatusCode.Ok
        || response.data.success === false
    ) {
        throw new Error(`Failed to get active experiments: ${response.data.error}`);
    }

    return response.data.data;
}

export async function getAgencySettings() {
    const response = await getBFFApiClient().get<APIResponse<AgencySettings>>(
        '/v1/me/agency-settings'
    );

    if (response.status !== HttpStatusCode.Ok || response.data.success === false) {
        throw new Error(`Failed to get agency settings: ${response.data.error}`);
    }

    return response.data.data;
}
